import React, { useLayoutEffect, useRef } from "react";

import { PromptInputTextarea } from "@src/components/ai-elements/prompt-input";

interface TextInputHandlerProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  submitDisabled?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export const TextInputHandler: React.FC<TextInputHandlerProps> = ({
  value,
  onChange,
  onSubmit,
  submitDisabled = false,
  placeholder = "Type your message...",
  disabled = false,
  className,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Grows the textarea to fit its content (clamped by the `max-h-48` CSS
  // already on `PromptInputTextarea`) instead of staying pinned to its
  // compact single-line height — `field-sizing: content` would do this
  // declaratively, but isn't reliably supported across the browsers this
  // app targets, so it's done by hand here.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (value.trim() && !submitDisabled) {
        onSubmit();
      }
    }
  };

  return (
    <PromptInputTextarea
      onChange={(e) => onChange(e.target.value)}
      ref={textareaRef}
      disabled={disabled}
      placeholder={placeholder}
      value={value}
      onKeyUp={handleKeyPress}
      className={className}
    />
  );
};
